import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import type { User } from '../types';
import { authAPI, usersAPI } from '../services/api';
import api from '../services/api';
import Toast from '../components/Toast';
import { utcTimeToLocal, localTimeToUTC } from '../utils/timezone';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface TimeRange {
  start: string;
  end: string;
}

interface DayWorkingHours {
  weekday: number;
  is_active: boolean;
  ranges: TimeRange[];
}

// Helper function to round time to nearest 30-minute interval
const roundToNearest30Minutes = (time: string): string => {
  if (!time || !time.includes(':')) return '09:00';
  const [hours, minutes] = time.split(':').map(Number);
  const roundedMinutes = Math.round(minutes / 30) * 30;
  if (roundedMinutes >= 60) {
    return `${(hours + 1).toString().padStart(2, '0')}:00`;
  }
  return `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
};

// Custom Time Picker Component with 30-minute intervals
interface TimePickerProps {
  value: string; // Format: "HH:MM"
  onChange: (value: string) => void;
  className?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, className = '' }) => {
  // Generate time options with 30-minute intervals (00:00 to 23:30)
  const timeOptions: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(timeString);
    }
  }

  // Format time for display (e.g., "09:00" -> "9:00 AM")
  const formatTimeForDisplay = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Ensure value is rounded to 30 minutes
  const roundedValue = value ? roundToNearest30Minutes(value) : '09:00';

  return (
    <select
      value={roundedValue}
      onChange={(e) => onChange(e.target.value)}
      className={`px-3 py-2 text-xs font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer shadow-sm hover:border-primary-400 transition-colors ${className}`}
    >
      {timeOptions.map((time) => (
        <option key={time} value={time}>
          {formatTimeForDisplay(time)}
        </option>
      ))}
    </select>
  );
};

// Inline Day Working Hours Editor Component
interface DayWorkingHoursEditorProps {
  dayName: string;
  weekday: number;
  isActive: boolean;
  ranges: TimeRange[];
  onToggle: () => void;
  onRangesChange: (ranges: TimeRange[]) => void;
}

const DayWorkingHoursEditor: React.FC<DayWorkingHoursEditorProps> = ({
  dayName,
  isActive,
  ranges,
  onToggle,
  onRangesChange,
}) => {
  const addRange = () => {
    onRangesChange([...ranges, { start: '09:00', end: '17:00' }]);
  };

  const removeRange = (index: number) => {
    onRangesChange(ranges.filter((_, i) => i !== index));
  };

  const updateRange = (index: number, field: 'start' | 'end', value: string) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    onRangesChange(newRanges);
  };

  return (
    <div className={`border-2 rounded-lg p-3 ${isActive ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={onToggle}
            className="mr-2 h-4 w-4 text-primary-600 rounded border-gray-300"
          />
          <span className={`font-semibold text-sm ${isActive ? 'text-primary-900' : 'text-gray-500'}`}>
            {dayName}
          </span>
        </label>
      </div>

      {isActive && (
        <div className="space-y-2">
          {ranges.map((range, index) => (
            <div key={index} className="flex items-center space-x-2">
              <TimePicker
                value={range.start}
                onChange={(value) => updateRange(index, 'start', value)}
                className="flex-1 min-w-0"
              />
              <span className="text-gray-500 text-xs font-medium">-</span>
              <TimePicker
                value={range.end}
                onChange={(value) => updateRange(index, 'end', value)}
                className="flex-1 min-w-0"
              />
              {ranges.length > 1 && (
                <button
                  onClick={() => removeRange(index)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                  title="Remove range"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addRange}
            className="w-full px-3 py-2 text-xs text-primary-700 bg-primary-100 hover:bg-primary-200 rounded-lg transition font-medium"
          >
            + Add Time Range
          </button>
        </div>
      )}
    </div>
  );
};

const UsersPageV2: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [editingWorkingHours, setEditingWorkingHours] = useState<{ [userId: number]: DayWorkingHours[] }>({});
  const [editingUserData, setEditingUserData] = useState<{ [userId: number]: Partial<User> }>({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  useEffect(() => {
    console.log('UsersPageV2 - Current user:', currentUser);
    console.log('UsersPageV2 - can_view_all_tasks:', currentUser?.can_view_all_tasks);
    console.log('UsersPageV2 - can_manage_users:', currentUser?.can_manage_users);
    loadUsers();
  }, [currentUser]);

  const loadUsers = async () => {
    try {
      console.log('Loading users from /api/users/...');
      const response = await api.get('/api/users/');
      console.log('Users API response:', response.data);
      console.log('Is array?', Array.isArray(response.data));
      
      // Handle both array and paginated response
      let usersData: User[] = [];
      if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        usersData = (response.data as any).results || [];
      }
      
      console.log('Users count:', usersData.length);
      console.log('Users data:', usersData);
      
      setUsers(usersData);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      console.error('Error response:', err.response?.data);
      setToast({ message: '✗ Failed to load users', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const initializeUserEditing = (user: User) => {
    // Initialize user data
    setEditingUserData(prev => ({
      ...prev,
      [user.id]: {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
        is_active: user.is_active ?? true,
        slack_user_id: user.slack_user_id ?? '',
        used_for_autotests: user.used_for_autotests ?? false
      }
    }));
    
    // Initialize working hours
    if (editingWorkingHours[user.id]) return; // Already initialized
    
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const defaultHours: DayWorkingHours[] = dayNames.map((name, idx) => ({
      weekday: idx,
      is_active: false,
      ranges: []
    }));
    
    // Load from user.working_hours if available
    // Convert from UTC (stored in DB) to local timezone for display
    if (user.working_hours && Array.isArray(user.working_hours)) {
      user.working_hours.forEach((dayData: any) => {
        const dayIndex = dayNames.indexOf(dayData.day);
        if (dayIndex >= 0) {
          defaultHours[dayIndex] = {
            weekday: dayIndex,
            is_active: dayData.is_active || false,
            ranges: dayData.time_ranges?.map((r: any) => {
              // Convert UTC time from DB to local timezone for display
              const localStart = utcTimeToLocal(r.start);
              const localEnd = utcTimeToLocal(r.end);
              return { 
                start: roundToNearest30Minutes(localStart), 
                end: roundToNearest30Minutes(localEnd) 
              };
            }) || []
          };
        }
      });
    }
    
    setEditingWorkingHours(prev => ({ ...prev, [user.id]: defaultHours }));
  };

  const handleDayToggle = (userId: number, dayIndex: number) => {
    setEditingWorkingHours(prev => {
      const userHours = [...(prev[userId] || [])];
      userHours[dayIndex] = {
        ...userHours[dayIndex],
        is_active: !userHours[dayIndex].is_active
      };
      return { ...prev, [userId]: userHours };
    });
  };

  const handleRangesChange = (userId: number, dayIndex: number, newRanges: TimeRange[]) => {
    setEditingWorkingHours(prev => {
      const userHours = [...(prev[userId] || [])];
      userHours[dayIndex] = {
        ...userHours[dayIndex],
        ranges: newRanges
      };
      return { ...prev, [userId]: userHours };
    });
  };

  const saveUser = async (userId: number) => {
    try {
      const userData = editingUserData[userId];
      const workingHoursData = editingWorkingHours[userId];
      
      if (!userData) {
        setToast({ message: '✗ No data to save', type: 'error' });
        return;
      }

      const effectiveRole = userData.role || users.find((user) => user.id === userId)?.role;
      if (effectiveRole === 'manager' && !userData.slack_user_id?.trim()) {
        setToast({ message: '⚠ Slack User ID is required for managers', type: 'warning' });
        return;
      }
      
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      
      // Prepare working hours in DB format
      // Convert from local timezone (user input) to UTC for storage in DB
      const dbWorkingHours = workingHoursData?.map((day) => ({
        day: dayNames[day.weekday],
        is_active: day.is_active,
        time_ranges: day.ranges.map(r => ({ 
          start: localTimeToUTC(r.start),  // Convert local time to UTC
          end: localTimeToUTC(r.end)        // Convert local time to UTC
        }))
      })) || [];
      
      // Update user info
      await api.patch(`/api/users/${userId}/`, {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role: userData.role,
        is_active: userData.is_active,
        slack_user_id: userData.slack_user_id,
        used_for_autotests: ['manager', 'admin', 'owner'].includes(effectiveRole || '') && Boolean(userData.used_for_autotests),
        working_hours: dbWorkingHours
      });
      
      setToast({ message: '✓ User updated successfully!', type: 'success' });
      setEditingUserId(null);
      loadUsers(); // Reload users list
    } catch (err: any) {
      console.error('Save user error:', err);
      setToast({ 
        message: `✗ Failed to save: ${err.response?.data?.detail || err.message}`, 
        type: 'error' 
      });
    }
  };

  const handleSyncForUser = async (userId: number) => {
    setSyncing(true);
    try {
      // TODO: Implement sync for specific user
      // For now, sync for all (Owner only)
      const result = await authAPI.syncGoogleCalendar();
      setToast({ 
        message: `✓ Synced ${result.total_events} events from Google Calendar`, 
        type: 'success' 
      });
    } catch (err: any) {
      setToast({ 
        message: `✗ Sync failed: ${err.response?.data?.error || err.message}`, 
        type: 'error' 
      });
    } finally {
      setSyncing(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const badges = {
      owner: { color: 'bg-purple-100 text-purple-800 border-purple-300', label: '👑 Owner', icon: '👑' },
      admin: { color: 'bg-primary-100 text-primary-700 border-primary-300', label: '👨‍💼 Admin', icon: '👨‍💼' },
      manager: { color: 'bg-green-100 text-green-800 border-green-300', label: '📅 Manager', icon: '📅' },
    };
    return badges[role as keyof typeof badges] || { color: 'bg-gray-100 text-gray-800 border-gray-300', label: role, icon: '👤' };
  };

  // Check if current user is Owner or Admin
  if (currentUser && !currentUser.can_view_all_tasks) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners and Administrators can manage users.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="mt-2 text-gray-600">
                {currentUser?.can_manage_users 
                  ? 'Manage users, roles, and working hours' 
                  : 'View users and manage their settings'}
              </p>
            </div>

            {/* Add User Button - Only for Owner */}
            {currentUser?.can_manage_users && (
              <button
                className="px-6 py-3 bg-accent-500 text-white rounded-xl hover:bg-accent-600 transition font-semibold shadow-lg"
                onClick={() => setShowAddUserModal(true)}
              >
                <svg className="h-5 w-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New User
              </button>
            )}
          </div>

          {/* Users Grid */}
          <div className="grid gap-6">
            {users.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-12 text-center text-gray-500">
                No users found
              </div>
            ) : (
              users.map((user) => {
                const badge = getRoleBadge(user.role);
                const isEditing = editingUserId === user.id;

                return (
                  <div
                    key={user.id}
                    className="bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-accent-300 transition overflow-hidden"
                  >
                    {/* User Header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {/* Avatar */}
                          <div className="h-14 w-14 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center shadow-lg">
                            <span className="text-2xl font-bold text-white">
                              {user.first_name?.[0] || user.username[0].toUpperCase()}
                            </span>
                          </div>

                          {/* User Info */}
                          <div>
                            <div className="flex items-center space-x-3">
                              <h3 className="text-xl font-bold text-gray-900">
                                {user.first_name} {user.last_name}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${badge.color}`}>
                                {badge.label}
                              </span>
                              {/* Active/Inactive Status */}
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${
                                user.is_active 
                                  ? 'bg-green-100 text-green-800 border-green-300'
                                  : 'bg-red-100 text-red-800 border-red-300'
                              }`}>
                                {user.is_active ? '✓ Active' : '⛔ Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              @{user.username} • {user.email}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                          {/* Sync Button - Only for Owner and only for Managers */}
                          {currentUser?.can_manage_users && user.role === 'manager' && user.has_calendar_access && (
                            <button
                              onClick={() => handleSyncForUser(user.id)}
                              disabled={syncing}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                syncing
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-purple-600 text-white hover:bg-purple-700 shadow'
                              }`}
                              title="Sync Google Calendar"
                            >
                              <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Sync
                            </button>
                          )}

                          {/* Edit/Manage Button */}
                          <button
                            onClick={() => {
                              if (!isEditing) {
                                initializeUserEditing(user);
                                setEditingUserId(user.id);
                              } else {
                                setEditingUserId(null);
                              }
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition shadow ${
                              isEditing
                                ? 'bg-orange-500 text-white'
                                : 'bg-accent-500 text-white hover:bg-accent-600'
                            }`}
                          >
                            {isEditing ? 'Close' : 'Manage'}
                          </button>

                          {/* Delete Button - Only for Admin and Owner */}
                          {currentUser?.can_view_all_tasks && user.id !== currentUser.id && (
                            <button
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete user ${user.username} (${user.email})? This action cannot be undone.`)) {
                                  try {
                                    await usersAPI.delete(user.id);
                                    setToast({ message: `✓ User ${user.username} deleted successfully`, type: 'success' });
                                    loadUsers(); // Reload the users list
                                  } catch (err: any) {
                                    console.error('Failed to delete user:', err);
                                    const errorMessage = err.response?.data?.error || err.response?.data?.detail || 'Failed to delete user';
                                    setToast({ message: `✗ ${errorMessage}`, type: 'error' });
                                  }
                                }
                              }}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium shadow"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expandable User Details */}
                    {isEditing && (
                      <div className="px-6 py-6 bg-gray-50 border-t border-gray-200 space-y-6">
                        
                        {/* BLOCK 1: Status & Google Calendar */}
                        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                          <h4 className="font-bold text-gray-900 mb-4 text-lg">📊 Status & Integration</h4>
                          <div className="grid grid-cols-2 gap-6">
                            {/* Active Status */}
                            <div className={`p-4 rounded-lg border-2 ${
                              editingUserData[user.id]?.is_active ?? user.is_active ?? true
                                ? 'bg-green-50 border-green-300'
                                : 'bg-red-50 border-red-300'
                            }`}>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  id={`active-${user.id}`}
                                  checked={editingUserData[user.id]?.is_active ?? user.is_active ?? true}
                                  onChange={(e) => setEditingUserData(prev => ({
                                    ...prev,
                                    [user.id]: { ...prev[user.id], is_active: e.target.checked }
                                  }))}
                                  className="h-5 w-5 text-accent-500 focus:ring-accent-500 border-gray-300 rounded"
                                />
                                <label htmlFor={`active-${user.id}`} className="text-base font-semibold text-gray-900">
                                  {editingUserData[user.id]?.is_active ?? user.is_active ?? true ? '✅ Active User' : '⛔ Inactive User'}
                                </label>
                              </div>
                              <p className="text-sm text-gray-600 mt-2 ml-8">
                                {editingUserData[user.id]?.is_active ?? user.is_active ?? true 
                                  ? 'User can log in and use the system' 
                                  : 'User is disabled and cannot log in'}
                              </p>
                            </div>

                            {/* Google Calendar Status - Only for Managers */}
                            {user.role === 'manager' && (
                              <div className={`p-4 rounded-lg border-2 ${
                                user.has_calendar_access
                                  ? 'bg-green-50 border-green-300'
                                  : 'bg-gray-50 border-gray-300'
                              }`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <p className="font-semibold text-gray-900">📅 Google Calendar</p>
                                    <p className="text-sm text-gray-600">
                                      {user.has_calendar_access ? '✓ Connected' : '○ Not Connected'}
                                    </p>
                                    {user.has_calendar_access && (user as any).google_calendar_timezone && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        🌍 Timezone: {(user as any).google_calendar_timezone}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {user.has_calendar_access && currentUser?.can_manage_users && (
                                  <button
                                    onClick={() => handleSyncForUser(user.id)}
                                    disabled={syncing}
                                    className="mt-2 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm font-medium"
                                  >
                                    <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Sync Now
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* BLOCK 2: User Information */}
                        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                          <h4 className="font-bold text-gray-900 mb-4 text-lg">👤 User Information</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                              <input
                                type="text"
                                value={editingUserData[user.id]?.first_name || ''}
                                onChange={(e) => setEditingUserData(prev => ({
                                  ...prev,
                                  [user.id]: { ...prev[user.id], first_name: e.target.value }
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                              <input
                                type="text"
                                value={editingUserData[user.id]?.last_name || ''}
                                onChange={(e) => setEditingUserData(prev => ({
                                  ...prev,
                                  [user.id]: { ...prev[user.id], last_name: e.target.value }
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input
                                type="email"
                                value={editingUserData[user.id]?.email || ''}
                                onChange={(e) => setEditingUserData(prev => ({
                                  ...prev,
                                  [user.id]: { ...prev[user.id], email: e.target.value }
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                              />
                            </div>

                            {/* Role Selector - Only for Owner */}
                            {currentUser?.can_manage_users && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                  value={editingUserData[user.id]?.role || user.role}
                                  onChange={(e) => setEditingUserData(prev => ({
                                    ...prev,
                                    [user.id]: { ...prev[user.id], role: e.target.value as any }
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                                >
                                  <option value="owner">👑 Owner</option>
                                  <option value="admin">👨‍💼 Admin</option>
                                  <option value="manager">📅 Manager</option>
                                </select>
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Slack User ID
                                {(editingUserData[user.id]?.role || user.role) === 'manager' && (
                                  <span className="text-red-500"> *</span>
                                )}
                              </label>
                              <input
                                type="text"
                                placeholder="U0123456789"
                                value={editingUserData[user.id]?.slack_user_id || ''}
                                onChange={(e) => setEditingUserData(prev => ({
                                  ...prev,
                                  [user.id]: { ...prev[user.id], slack_user_id: e.target.value }
                                }))}
                                required={(editingUserData[user.id]?.role || user.role) === 'manager'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Used to @-mention this manager in Slack lead notifications instead of showing their email.
                                Find it in Slack: profile photo → View profile → ⋮ → Copy member ID.
                              </p>
                            </div>

                            {['manager', 'admin', 'owner'].includes(editingUserData[user.id]?.role || user.role) && (
                              <label className="flex items-start gap-3 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editingUserData[user.id]?.used_for_autotests ?? false}
                                  onChange={(e) => setEditingUserData(prev => ({
                                    ...prev,
                                    [user.id]: { ...prev[user.id], used_for_autotests: e.target.checked }
                                  }))}
                                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>
                                  <span className="block text-sm font-semibold text-gray-900">Use for website autotests</span>
                                  <span className="mt-1 block text-xs text-gray-600">
                                    Managers stay in regular rotation. An opted-in owner or admin takes priority for autotests and exposes an always-available synthetic schedule.
                                  </span>
                                </span>
                              </label>
                            )}
                          </div>
                        </div>

                        {/* BLOCK 3: Working Hours - Only for Managers */}
                        {user.role === 'manager' && (
                          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                            <h4 className="font-bold text-gray-900 mb-4 text-lg">⏰ Working Hours Configuration (TIMEZONE: UTC)</h4>
                            <p className="text-sm text-gray-600 mb-4">Configure manager's weekly availability by clicking on days and setting time ranges. Time intervals are in 30-minute increments.</p>
                            
                            {editingWorkingHours[user.id] ? (
                              <div className="space-y-3">
                                {/* First Row: Mon-Thu (4 columns) */}
                                <div className="grid grid-cols-4 gap-2">
                                  {editingWorkingHours[user.id].slice(0, 4).map((dayData, dayIndex) => {
                                    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu'];
                                    return (
                                      <DayWorkingHoursEditor
                                        key={dayIndex}
                                        dayName={dayNames[dayIndex]}
                                        weekday={dayIndex}
                                        isActive={dayData.is_active}
                                        ranges={dayData.ranges}
                                        onToggle={() => handleDayToggle(user.id, dayIndex)}
                                        onRangesChange={(newRanges: TimeRange[]) => handleRangesChange(user.id, dayIndex, newRanges)}
                                      />
                                    );
                                  })}
                                </div>
                                
                                {/* Second Row: Fri-Sun (3 columns) */}
                                <div className="grid grid-cols-3 gap-2">
                                  {editingWorkingHours[user.id].slice(4, 7).map((dayData, idx) => {
                                    const dayIndex = idx + 4;
                                    const dayNames = ['Fri', 'Sat', 'Sun'];
                                    return (
                                      <DayWorkingHoursEditor
                                        key={dayIndex}
                                        dayName={dayNames[idx]}
                                        weekday={dayIndex}
                                        isActive={dayData.is_active}
                                        ranges={dayData.ranges}
                                        onToggle={() => handleDayToggle(user.id, dayIndex)}
                                        onRangesChange={(newRanges: TimeRange[]) => handleRangesChange(user.id, dayIndex, newRanges)}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <p className="text-gray-500">Loading working hours...</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Save Button */}
                        <div className="mt-6 flex items-center justify-end space-x-3">
                          <button
                            onClick={() => setEditingUserId(null)}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveUser(user.id)}
                            className="px-6 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition font-semibold shadow"
                          >
                            💾 Save Changes
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onUserCreated={() => {
            setShowAddUserModal(false);
            loadUsers();
          }}
          setToast={setToast}
        />
      )}
    </>
  );
};

// Add User Modal Component
interface AddUserModalProps {
  onClose: () => void;
  onUserCreated: () => void;
  setToast: (toast: { message: string; type: ToastType }) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onUserCreated, setToast }) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    role: 'manager' as 'manager' | 'admin' | 'owner',
    slack_user_id: '',
    used_for_autotests: false,
    password: '',
    password_confirm: ''
  });
  const [loading, setLoading] = useState(false);

  // Check if password fields should be shown (for admin and owner)
  const needsPassword = formData.role === 'admin' || formData.role === 'owner';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.email.trim()) {
      setToast({ message: '⚠ Please enter an email address', type: 'warning' });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setToast({ message: '⚠ Please enter a valid email address', type: 'warning' });
      return;
    }

    if (formData.role === 'manager' && !formData.slack_user_id.trim()) {
      setToast({ message: '⚠ Slack User ID is required for managers', type: 'warning' });
      return;
    }

    // Password validation for admin and owner
    if (needsPassword) {
      if (!formData.password) {
        setToast({ message: '⚠ Password is required for admin and owner roles', type: 'warning' });
        return;
      }
      if (formData.password !== formData.password_confirm) {
        setToast({ message: '⚠ Passwords do not match', type: 'warning' });
        return;
      }
      if (formData.password.length < 8) {
        setToast({ message: '⚠ Password must be at least 8 characters long', type: 'warning' });
        return;
      }
    }

    setLoading(true);
    try {
      // Use username from email if not provided
      const username = formData.username.trim() || formData.email.split('@')[0];
      
      // For managers, use register-email endpoint (Google SSO only)
      // For admin and owner, use users API endpoint (with password)
      if (formData.role === 'manager') {
        await api.post('/api/auth/register-email/', {
          email: formData.email.trim(),
          role: formData.role,
          slack_user_id: formData.slack_user_id.trim(),
          used_for_autotests: formData.used_for_autotests
        });
        setToast({ 
          message: '✓ User email registered! They can now log in with Google SSO using this email.', 
          type: 'success' 
        });
      } else {
        // Create admin/owner with password
        await usersAPI.create({
          email: formData.email.trim(),
          username: username,
          first_name: formData.first_name.trim() || '',
          last_name: formData.last_name.trim() || '',
          role: formData.role,
          used_for_autotests: ['admin', 'owner'].includes(formData.role) && formData.used_for_autotests,
          password: formData.password,
          password_confirm: formData.password_confirm
        });
        setToast({ 
          message: `✓ ${formData.role === 'admin' ? 'Administrator' : 'Owner'} user created successfully! They can now log in with username/email and password.`, 
          type: 'success' 
        });
      }
      onUserCreated();
    } catch (err: any) {
      console.error('Create user error:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || err.response?.data?.password?.[0] || err.response?.data?.password_confirm?.[0] || 'Failed to create user';
      setToast({ message: `✗ ${errorMsg}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-primary-50 to-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Register New User</h2>
            <p className="text-sm text-gray-600 mt-1">Add email to whitelist for Google SSO access</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Info Banner */}
        <div className="px-6 py-4 bg-primary-50 border-b border-primary-200">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-primary-700">
              <p className="font-semibold mb-2">How it works:</p>
              {formData.role === 'manager' ? (
                <ol className="space-y-1 ml-4 list-decimal">
                  <li>Add the user's email to the system</li>
                  <li>Assign them Manager role</li>
                  <li>User logs in via Google SSO with this email</li>
                  <li>Their name and username are auto-populated from Google profile ✨</li>
                </ol>
              ) : (
                <ol className="space-y-1 ml-4 list-decimal">
                  <li>Enter user's email and set a password</li>
                  <li>Assign them {formData.role === 'admin' ? 'Administrator' : 'Owner'} role</li>
                  <li>User can log in with username/email and password</li>
                  <li>They can also use Google SSO if needed</li>
                </ol>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6">
          <div className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.doe@example.com"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                required
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                ✉️ User will log in with this email via Google SSO
              </p>
            </div>

            {/* Username (optional, defaults to email prefix) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username (optional)
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Auto-generated from email if not provided"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
              />
              <p className="text-xs text-gray-500 mt-1">
                If not provided, username will be generated from email (part before @)
              </p>
            </div>

            {/* First Name and Last Name (optional) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name (optional)
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name (optional)
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value as 'manager' | 'admin' | 'owner';
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    // Clear password fields when switching to manager
                    password: newRole === 'manager' ? '' : formData.password,
                    password_confirm: newRole === 'manager' ? '' : formData.password_confirm
                  });
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
              >
                <option value="manager">👤 Manager</option>
                <option value="admin">👥 Administrator</option>
                <option value="owner">👑 Owner</option>
              </select>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                <p><strong>Manager:</strong> Manages own calendar and tasks (Google SSO only)</p>
                <p><strong>Admin:</strong> Views all calendars, manages users, can login with password</p>
                <p><strong>Owner:</strong> Full system control, can add/delete users, can login with password</p>
              </div>
            </div>

            {/* Slack member ID (required for managers) */}
            {['manager', 'admin'].includes(formData.role) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slack User ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.slack_user_id}
                  onChange={(e) => setFormData({ ...formData, slack_user_id: e.target.value })}
                  placeholder="U0123456789"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find it in Slack: profile photo → View profile → ⋮ → Copy member ID.
                </p>
              </div>
            )}

            {['manager', 'admin', 'owner'].includes(formData.role) && (
              <label className="flex items-start gap-3 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.used_for_autotests}
                  onChange={(e) => setFormData({ ...formData, used_for_autotests: e.target.checked })}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">Use for website autotests</span>
                  <span className="mt-1 block text-xs text-gray-600">
                    Managers stay in regular rotation. An opted-in owner or admin receives autotests through an always-available synthetic schedule.
                  </span>
                </span>
              </label>
            )}

            {/* Password fields (only for admin and owner) */}
            {needsPassword && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password (min 8 characters)"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                    required={needsPassword}
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.password_confirm}
                    onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                    placeholder="Confirm password"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-lg"
                    required={needsPassword}
                    minLength={8}
                  />
                </div>
              </>
            )}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 rounded-lg transition font-semibold shadow-lg ${
                loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-accent-500 text-white hover:bg-accent-600'
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Registering...
                </span>
              ) : (
                'Register Email'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPageV2;
